/**
 * Hand-curated theme presets — kept separate from the generated
 * `themePresets.ts` (which the import script overwrites).
 *
 * Palettes are adapted from a few production design systems surfaced via
 * refero.design (styles.refero.design). Only the *token values* (hex colors)
 * are used and every preset is given a NEUTRAL label — no brand names or
 * logos — to avoid trademark exposure in a bank product. Proprietary display
 * fonts (CursorGothic, Arcadia, Control…) are intentionally dropped; only
 * fonts in our own catalog (e.g. Inter) are carried, otherwise the theme
 * falls back to our defaults. refero is a commercial beta — confirm their
 * terms before pulling more.
 *
 * Mapping mirrors the open-design importer: brand = primary action color,
 * surface = page background, onSurface = body text, contrast = inverted
 * surface (works for light and dark), brandHover ≈ brand darkened ~8%.
 */
import type { ThemePreset } from "./themePresets";

export const MANUAL_THEME_PRESETS: ThemePreset[] = [
  {
    id: "warm-editorial",
    name: "Warm Editorial",
    aesthetic: "warm-editorial",
    tokens: {
      colors: {
        brand: "#f54e00",
        brandHover: "#e14800",
        onBrand: "#ffffff",
        accent: "#c08532",
        onAccent: "#ffffff",
        surface: "#f7f7f4",
        onSurface: "#26251e",
        contrast: "#141414",
        onContrast: "#f7f7f4",
        success: "#34785c",
      },
    },
  },
  {
    id: "indigo-midnight",
    name: "Indigo Midnight",
    aesthetic: "indigo-midnight",
    tokens: {
      colors: {
        brand: "#5266eb",
        brandHover: "#4b5ed8",
        onBrand: "#ffffff",
        accent: "#cdddff",
        onAccent: "#171721",
        surface: "#171721",
        onSurface: "#ededf3",
        contrast: "#ededf3",
        onContrast: "#171721",
      },
    },
  },
  {
    id: "graphite-dark",
    name: "Graphite Dark",
    aesthetic: "graphite-dark",
    tokens: {
      colors: {
        brand: "#5e6ad2",
        brandHover: "#5662c1",
        onBrand: "#ffffff",
        accent: "#5e6ad2",
        onAccent: "#ffffff",
        surface: "#08090a",
        onSurface: "#f7f8f8",
        contrast: "#f7f8f8",
        onContrast: "#08090a",
        success: "#27a644",
        danger: "#eb5757",
      },
      font: { display: "inter", body: "inter" },
    },
  },
  {
    id: "sky-light",
    name: "Sky Light",
    aesthetic: "sky-light",
    tokens: {
      colors: {
        brand: "#2b7fff",
        brandHover: "#2875eb",
        onBrand: "#ffffff",
        accent: "#2b7fff",
        onAccent: "#ffffff",
        surface: "#ffffff",
        onSurface: "#1b1b1b",
        contrast: "#1b1b1b",
        onContrast: "#ffffff",
      },
    },
  },

  /* ----------------------------------------------------------------------- *
   * STYLE presets — these go beyond color: each pairs a palette with the
   * `design` tokens (radius / shadow / surface / fontScale / border) so the
   * SAME components render in unmistakably different forms. See
   * ThemeStyleInjector for how `design` maps to `--dl-*` vars.
   * ----------------------------------------------------------------------- */
  {
    id: "glassmorphism",
    name: "Glassmorphism",
    aesthetic: "glass",
    tokens: {
      colors: {
        brand: "#6366f1",
        brandHover: "#5457e0",
        onBrand: "#ffffff",
        accent: "#a855f7",
        onAccent: "#ffffff",
        surface: "#e7ecff",
        onSurface: "#1a1c2e",
        contrast: "#16182b",
        onContrast: "#eef1ff",
        success: "#10b981",
      },
      font: { display: "instrument-serif", body: "manrope", mono: "geist-mono" },
      design: {
        radius: "18px",
        shadow: "soft",
        surface: "glass",
        fontScale: 1.05,
        border: "hairline",
      },
    },
  },
  {
    id: "bento",
    name: "Bento",
    aesthetic: "bento",
    recipe: "bento",
    tokens: {
      colors: {
        brand: "#ff6b35",
        brandHover: "#ec5f2e",
        onBrand: "#ffffff",
        accent: "#2a9d8f",
        onAccent: "#ffffff",
        surface: "#f6f5f1",
        onSurface: "#22201c",
        contrast: "#1c1b18",
        onContrast: "#f6f5f1",
        success: "#2a9d8f",
      },
      font: { display: "fraunces", body: "dm-sans", mono: "jetbrains-mono" },
      design: {
        radius: "24px",
        shadow: "soft",
        surface: "flat",
        fontScale: 1.05,
        border: "hairline",
      },
    },
  },
  {
    id: "big-type",
    name: "Big Type",
    aesthetic: "big-type",
    tokens: {
      colors: {
        brand: "#111111",
        brandHover: "#000000",
        onBrand: "#ffffff",
        accent: "#e23636",
        onAccent: "#ffffff",
        surface: "#fbfaf8",
        onSurface: "#0f0f0f",
        contrast: "#0f0f0f",
        onContrast: "#fbfaf8",
      },
      font: { display: "playfair", body: "inter", mono: "space-mono" },
      design: {
        radius: "4px",
        shadow: "none",
        surface: "flat",
        fontScale: 1.35,
        border: "hairline",
      },
    },
  },
  {
    id: "brutalist",
    name: "Brutalist",
    aesthetic: "brutalist",
    recipe: "brutalist",
    tokens: {
      colors: {
        brand: "#ffe600",
        brandHover: "#f2d900",
        onBrand: "#111111",
        accent: "#ff3b30",
        onAccent: "#ffffff",
        surface: "#ffffff",
        onSurface: "#111111",
        contrast: "#111111",
        onContrast: "#ffffff",
        danger: "#ff3b30",
      },
      font: { display: "dm-serif", body: "ibm-plex-sans", mono: "space-mono" },
      design: {
        radius: "0px",
        shadow: "hard",
        surface: "flat",
        fontScale: 1.15,
        border: "bold",
      },
    },
  },
  {
    id: "soft-pop",
    name: "Soft Pop",
    aesthetic: "soft-pop",
    tokens: {
      colors: {
        brand: "#7c5cff",
        brandHover: "#6e4ff0",
        onBrand: "#ffffff",
        accent: "#ff5da2",
        onAccent: "#ffffff",
        surface: "#fff7fb",
        onSurface: "#2b1f33",
        contrast: "#2b1f33",
        onContrast: "#fff7fb",
        success: "#22c55e",
      },
      font: { display: "fraunces", body: "manrope", mono: "geist-mono" },
      design: {
        radius: "28px",
        shadow: "soft",
        surface: "flat",
        fontScale: 1.1,
        border: "none",
      },
    },
  },
  {
    id: "editorial-serif",
    name: "Editorial",
    aesthetic: "editorial",
    recipe: "editorial",
    tokens: {
      colors: {
        brand: "#1a1a1a",
        brandHover: "#000000",
        onBrand: "#ffffff",
        accent: "#8a5a2b",
        onAccent: "#ffffff",
        surface: "#f5f1e8",
        onSurface: "#211d17",
        contrast: "#211d17",
        onContrast: "#f5f1e8",
      },
      font: { display: "cormorant", body: "ibm-plex-sans", mono: "ibm-plex-mono" },
      design: {
        radius: "2px",
        shadow: "none",
        surface: "flat",
        fontScale: 1.25,
        border: "hairline",
      },
    },
  },
  {
    id: "gradient-vivid",
    name: "Gradient",
    aesthetic: "gradient",
    tokens: {
      colors: {
        brand: "#7c3aed",
        brandHover: "#6d28d9",
        onBrand: "#ffffff",
        accent: "#ec4899",
        onAccent: "#ffffff",
        surface: "#faf5ff",
        onSurface: "#1e1233",
        contrast: "#1b1033",
        onContrast: "#f5ecff",
        success: "#06b6d4",
      },
      font: { display: "fraunces", body: "geist", mono: "geist-mono" },
      design: {
        radius: "14px",
        shadow: "soft",
        surface: "gradient",
        fontScale: 1.05,
        border: "hairline",
      },
    },
  },
  {
    id: "corporate-clean",
    name: "Corporate",
    aesthetic: "corporate",
    tokens: {
      colors: {
        brand: "#1d4ed8",
        brandHover: "#1b46c2",
        onBrand: "#ffffff",
        accent: "#0f766e",
        onAccent: "#ffffff",
        surface: "#ffffff",
        onSurface: "#1f2937",
        contrast: "#0f172a",
        onContrast: "#f8fafc",
        success: "#15803d",
        warning: "#b45309",
        danger: "#b91c1c",
      },
      font: { display: "ibm-plex-sans", body: "ibm-plex-sans", mono: "ibm-plex-mono" },
      design: {
        radius: "6px",
        shadow: "soft",
        surface: "flat",
        fontScale: 1,
        border: "hairline",
      },
    },
  },
  {
    id: "swiss",
    name: "Swiss",
    aesthetic: "swiss",
    tokens: {
      colors: {
        brand: "#111111",
        brandHover: "#000000",
        onBrand: "#ffffff",
        accent: "#e2231a",
        onAccent: "#ffffff",
        surface: "#ffffff",
        onSurface: "#111111",
        contrast: "#111111",
        onContrast: "#ffffff",
        danger: "#e2231a",
      },
      font: { display: "space-grotesk", body: "work-sans", mono: "space-mono" },
      design: {
        radius: "2px",
        shadow: "none",
        surface: "flat",
        fontScale: 1.1,
        border: "hairline",
      },
    },
  },
  {
    id: "dark-mesh",
    name: "Dark Mesh",
    aesthetic: "dark-mesh",
    tokens: {
      colors: {
        brand: "#6ea8ff",
        brandHover: "#5b97f2",
        onBrand: "#0b0d12",
        accent: "#b98bff",
        onAccent: "#0b0d12",
        surface: "#0b0d12",
        onSurface: "#eef1f8",
        contrast: "#eef1f8",
        onContrast: "#0b0d12",
        success: "#34d399",
      },
      font: { display: "space-grotesk", body: "plus-jakarta", mono: "geist-mono" },
      design: {
        radius: "16px",
        shadow: "soft",
        surface: "gradient",
        fontScale: 1.05,
        border: "hairline",
      },
    },
  },
];
